import {
  AccountBalance,
  AccountBalanceWallet,
  Apps,
  Article,
  BlurCircular,
  CompareArrows,
  ControlPointDuplicate,
  DocumentScannerOutlined,
  GroupWorkOutlined,
  Home,
  IntegrationInstructions,
  LocationCity,
  MailOutline,
  Payments,
  People,
  ReceiptLong,
  RequestQuote,
  Sensors,
  SettingsInputAntenna,
  Speed,
  VpnKey,
  WbSunny,
  WifiTetheringOff,
} from '@mui/icons-material';
import { SvgIconProps } from '@mui/material';
import { ComponentType } from 'react';
import { GlobalSearchCategory } from 'switch-shared/modules/features/global-search/global-search.dto';

/**
 * The set of entities/sections that have a canonical icon. The values mirror
 * `GlobalSearchCategory` where they overlap, plus the extra navigation sections
 * that aren't searchable entities.
 */
export type EntityType =
  | 'applicationGroups'
  | 'applications'
  | 'users'
  | 'apiKeys'
  | 'tariffs'
  | 'accounts'
  | 'utilities'
  | 'meterPoints'
  | 'meters'
  | 'gateways'
  | 'sensors'
  | 'zones'
  | 'unallocatedDevices'
  | 'manualReadings'
  | 'dashboard'
  | 'accounting'
  | 'providers'
  | 'reporting'
  | 'integrations'
  | 'payments'
  | 'invoices'
  | 'reconciliations'
  | 'emailIntegrationLogs';

/**
 * Single source of truth for entity icons, shared across the sidebar, global
 * search, dashboard widgets, etc. Keep this in sync if a new entity is added.
 */
const ICON_BY_ENTITY: Record<EntityType, ComponentType<SvgIconProps>> = {
  applicationGroups: BlurCircular,
  applications: Apps,
  users: People,
  apiKeys: VpnKey,
  tariffs: RequestQuote,
  accounts: AccountBalance,
  utilities: WbSunny,
  meterPoints: ControlPointDuplicate,
  meters: Speed,
  gateways: SettingsInputAntenna,
  sensors: Sensors,
  zones: GroupWorkOutlined,
  unallocatedDevices: WifiTetheringOff,
  manualReadings: DocumentScannerOutlined,
  dashboard: Home,
  accounting: AccountBalanceWallet,
  providers: LocationCity,
  reporting: Article,
  integrations: IntegrationInstructions,
  payments: Payments,
  invoices: ReceiptLong,
  reconciliations: CompareArrows,
  emailIntegrationLogs: MailOutline,
};

export interface EntityIconProps extends SvgIconProps {
  /**
   * The entity/section to render the icon for. Accepts an {@link EntityType}
   * literal or a {@link GlobalSearchCategory} (their string values align).
   */
  type: EntityType | GlobalSearchCategory;
}

/** Renders the canonical MUI icon for a given entity type. */
const EntityIcon = ({
  type,
  ...props
}: EntityIconProps): JSX.Element | null => {
  const Icon = ICON_BY_ENTITY[type as EntityType];
  return Icon ? <Icon {...props} /> : null;
};

export default EntityIcon;
